import { useEffect, useRef, useState } from 'react'
import { getAllPlanetData, getAllHouseData, getPanchang, buildAvakhada } from '@astro/vedastro'
import { Badge, InfoRow, PillGroup, PLANET_GLYPHS, StudioError, TableSkeleton } from '../StudioBits'

const VIEWS = [
  { key: 'planets', label: 'Planets', icon: '🪐' },
  { key: 'houses', label: 'Houses · Bhava', icon: '🏛️' },
  { key: 'avakhada', label: 'Avakhada', icon: '📿' },
]

const DIGNITY_TONES = {
  Exalted: 'green', Moolatrikona: 'gold', 'Own Sign': 'gold',
  Debilitated: 'red', 'Friend Sign': 'green', 'Enemy Sign': 'amber', Neutral: 'muted',
}

function fmtDeg(dms) {
  return dms ? dms.replace(/(\d+)° (\d+)' (\d+)/, "$1° $2′") : '—'
}

function PlanetsTable({ planets }) {
  return (
    <div className="ks-table-wrap">
      <table className="ks-table">
        <thead>
          <tr>
            <th>Graha</th><th>Sign</th><th>Degree</th><th>Sign Lord</th><th>Nakshatra</th>
            <th>Nak. Lord</th><th>House</th><th>Navamsha</th><th>Dignity</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {planets.map((p) => (
            <tr key={p.name}>
              <td>
                <span className="ks-planet-cell">
                  <span className="ks-planet-glyph">{PLANET_GLYPHS[p.name] || '✦'}</span>
                  {p.name}{p.retrograde && <sup className="ks-retro">℞</sup>}
                </span>
              </td>
              <td>{p.sign || '—'}</td>
              <td className="ks-mono">{fmtDeg(p.dms)}</td>
              <td>{p.signLord}</td>
              <td>{p.nakshatra}{p.pada && <span className="ks-dim"> · {p.pada}</span>}</td>
              <td>{p.nakshatraLord}</td>
              <td className="ks-center">{p.house ?? '—'}</td>
              <td>{p.navamsaSign || '—'}</td>
              <td><Badge tone={DIGNITY_TONES[p.dignity] || 'muted'}>{p.dignity}</Badge></td>
              <td>
                <span className="ks-badges">
                  {p.combust && <Badge tone="red">Combust</Badge>}
                  {p.vargottama && <Badge tone="gold">Vargottama</Badge>}
                  <Badge tone={p.benefic ? 'green' : 'amber'}>{p.benefic ? 'Benefic' : 'Malefic'}</Badge>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function HousesTable({ houses }) {
  return (
    <div className="ks-table-wrap">
      <table className="ks-table">
        <thead>
          <tr>
            <th>Bhava</th><th>Cusp Sign</th><th>Degree Span</th><th>Lord</th>
            <th>Cusp Nakshatra</th><th>Planets In</th><th>Aspected By</th><th>Strength</th>
          </tr>
        </thead>
        <tbody>
          {houses.map((h) => (
            <tr key={h.house}>
              <td>
                <span className="ks-planet-cell">
                  <span className="ks-house-num">{h.house}</span>
                  {h.label}
                </span>
              </td>
              <td>{h.sign || '—'}<span className="ks-dim"> · {fmtDeg(h.dms)}</span></td>
              <td className="ks-mono">
                {h.begin != null && h.end != null ? `${h.begin.toFixed(1)}° – ${h.end.toFixed(1)}°` : '—'}
              </td>
              <td>{h.lord}</td>
              <td>{h.nakshatra}{h.pada && <span className="ks-dim"> · {h.pada}</span>}</td>
              <td>{h.planets.length ? h.planets.join(', ') : <span className="ks-dim">—</span>}</td>
              <td>{h.aspectedBy.length ? h.aspectedBy.join(', ') : <span className="ks-dim">—</span>}</td>
              <td>
                <Badge tone={h.strengthCategory === 'Strong' ? 'green' : h.strengthCategory === 'Weak' ? 'red' : 'muted'}>
                  {h.strengthCategory}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AvakhadaGrid({ avk }) {
  return (
    <>
      <div className="ks-avk-grid">
        <InfoRow label="Ascendant (Lagna)" value={`${avk.lagnaSa} · ${avk.lagna}`} accent />
        <InfoRow label="Rashi (Moon Sign)" value={`${avk.moonSignSa} · ${avk.moonSign}`} accent />
        <InfoRow label="Rashi Lord" value={avk.moonSignLord} />
        <InfoRow label="Nakshatra · Charan" value={`${avk.nakshatra} · Pada ${avk.pada}`} accent />
        <InfoRow label="Nakshatra Lord" value={avk.nakshatraLord} />
        <InfoRow label="Sun Sign (Surya Rashi)" value={`${avk.sunSignSa} · ${avk.sunSign}`} />
        <InfoRow label="Birth Tithi" value={avk.paksha ? `${avk.tithi} (${avk.paksha} Paksha)` : avk.tithi} />
        <InfoRow label="Birth Yoga" value={avk.yoga} />
        <InfoRow label="Birth Karana" value={avk.karana} />
        <InfoRow label="Varna" value={avk.varna} />
        <InfoRow label="Vashya" value={avk.vashya} />
        <InfoRow label="Yoni" value={avk.yoni} />
        <InfoRow label="Gan" value={avk.gana} />
        <InfoRow label="Nadi" value={avk.nadi} />
      </div>
      <p className="ks-note">
        Avakhada values follow the classical tables from your Moon nakshatra and rashi — the same details used for
        naming ceremonies and Guna Milan.
      </p>
    </>
  )
}

/**
 * Calculations — three sub-views over two paid calls (all planets, all houses)
 * plus the birth-time panchanga: full graha table, bhava table and Avakhada.
 */
export default function CalculationsPart({ birth }) {
  const [view, setView] = useState('planets')
  const [status, setStatus] = useState('loading') // loading | error | done
  const [data, setData] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const reqRef = useRef(0)

  const load = () => {
    const my = ++reqRef.current
    setStatus('loading')
    const args = { dob: birth.dob, tob: birth.tob, location: birth.location }
    Promise.all([
      getAllPlanetData(args),
      getAllHouseData(args),
      // Best-effort: without it the Avakhada tithi/yoga/karana rows show dashes.
      getPanchang({ date: birth.dob, time: birth.tob, location: birth.location }).catch(() => null),
    ])
      .then(([planets, houses, panchang]) => {
        if (my !== reqRef.current) return
        setData({ planets, houses, avakhada: buildAvakhada({ planets, houses, panchang }) })
        setStatus('done')
      })
      .catch((err) => {
        if (my !== reqRef.current || err?.name === 'AbortError') return
        setErrorMsg(err?.message || 'We could not compute the tables right now. Please try again.')
        setStatus('error')
      })
  }
  useEffect(load, [birth]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="ks-controls">
        <PillGroup ariaLabel="Calculation view" options={VIEWS} value={view} onChange={setView} />
      </div>

      {status === 'loading' && <TableSkeleton rows={view === 'houses' ? 12 : 9} cols={view === 'avakhada' ? 2 : 8} />}
      {status === 'error' && <StudioError text={errorMsg} onRetry={load} />}
      {status === 'done' && data && (
        <>
          {view === 'planets' && <PlanetsTable planets={data.planets} />}
          {view === 'houses' && <HousesTable houses={data.houses} />}
          {view === 'avakhada' && <AvakhadaGrid avk={data.avakhada} />}
        </>
      )}
    </div>
  )
}
